import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import AppErrorBoundary from "./components/AppErrorBoundary";
import {
  isCloudHost,
  shouldEnableLocalOfflineSupport,
} from "./utils/runtimeHost";
import { ASSET_REV } from "./version";
import { resetStaleLocalOfflineCache } from "./utils/localOfflineCache";

const ADMIN_THEME_STORAGE_KEY = "moo_admin_theme";
const ADMIN_THEME_USER_STORAGE_KEY = "moo_admin_theme_user";
const DEBUG_LOGS_STORAGE_KEY = "moo_debug_logs";

const resolveInitialTheme = () => {
  try {
    const storedTheme = window.localStorage.getItem(ADMIN_THEME_STORAGE_KEY);
    const isUserSet = window.localStorage.getItem(ADMIN_THEME_USER_STORAGE_KEY) === "1";
    if (isUserSet && (storedTheme === "dark" || storedTheme === "light")) {
      return storedTheme;
    }
  } catch (_e) {
    // ignore
  }
  return "dark";
};

const applyRuntimeBranding = () => {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  const host = window.location?.hostname || "";
  const cloud = isCloudHost(host);

  const setThemeColor = (color) => {
    const meta =
      document.querySelector('meta[name="theme-color"]') ||
      (() => {
        const created = document.createElement("meta");
        created.setAttribute("name", "theme-color");
        document.head.appendChild(created);
        return created;
      })();
    meta.setAttribute("content", color);
  };

  const setLinkHref = (rel, href) => {
    const link =
      document.querySelector(`link[rel="${rel}"]`) ||
      (() => {
        const created = document.createElement("link");
        created.setAttribute("rel", rel);
        document.head.appendChild(created);
        return created;
      })();
    link.setAttribute("href", href);
    return link;
  };

  if (cloud) {
    setThemeColor("#0e0527");
    setLinkHref("icon", "/pwa-icon-192-v2.png");
    setLinkHref("apple-touch-icon", "/apple-touch-icon-v2.png");
  }
};

applyRuntimeBranding();

// Appliquer le thème avant le premier rendu (évite le flash au chargement)
if (typeof document !== "undefined") {
  const root = document.documentElement;
  const initialTheme = resolveInitialTheme();
  root.classList.toggle("dark", initialTheme === "dark");
  root.style.colorScheme = initialTheme === "dark" ? "dark" : "light";
}

// Réduction du bruit console pour garder une caisse fluide
if (typeof window !== "undefined") {
  let verboseLogsEnabled = false;
  try {
    const debugFlagStored =
      window.localStorage.getItem(DEBUG_LOGS_STORAGE_KEY) === "1";
    const debugFlagInUrl = window.location?.search?.includes("debug=1");
    verboseLogsEnabled = debugFlagStored && debugFlagInUrl;
  } catch (_e) {
    verboseLogsEnabled = false;
  }

  if (!verboseLogsEnabled) {
    const noop = () => {};
    // Conserver warn/error pour le diagnostic utilisateur
    console.log = noop;
    console.info = noop;
    console.debug = noop;
  }
}


const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <AppErrorBoundary>
    <App />
  </AppErrorBoundary>
);

if (shouldEnableLocalOfflineSupport()) {
  window.addEventListener("load", async () => {
    if (process.env.NODE_ENV === "development") {
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
        .then(() => caches?.keys?.())
        .then((keys) => Promise.all((keys || []).map((key) => caches.delete(key))))
        .catch((error) => console.warn("Nettoyage service worker dev indisponible:", error));
      return;
    }

    try {
      await resetStaleLocalOfflineCache({
        assetRev: ASSET_REV,
        storage: window.localStorage,
        cacheStorage: window.caches,
        serviceWorker: navigator.serviceWorker,
      });
      const registration = await navigator.serviceWorker.register("/moo-local-sw.js");
      await registration.update().catch(() => {});
    } catch (error) {
      console.warn("Service worker local indisponible:", error);
    }
  });
}

// Auto-récupération si un chunk JS est manquant (après mise à jour ou cache).
if (typeof window !== "undefined") {
  const reloadOnce = () => {
    try {
      if (sessionStorage.getItem("moo_chunk_reload") === "1") return;
      sessionStorage.setItem("moo_chunk_reload", "1");
    } catch (_e) {
      // ignore storage errors
    }
    window.location.reload();
  };

  const isChunkError = (message) => {
    const msg = String(message || "").toLowerCase();
    return (
      msg.includes("chunkloaderror") ||
      msg.includes("loading chunk") ||
      msg.includes("failed to fetch dynamically imported module") ||
      msg.includes("loading css chunk")
    );
  };

  window.addEventListener("error", (event) => {
    if (isChunkError(event?.message || event?.error?.message)) {
      reloadOnce();
    }
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkError(event?.reason?.message || event?.reason)) {
      reloadOnce();
    }
  });
}
